import { createSlice } from "@reduxjs/toolkit";
import { includes, map, merge, update } from "lodash";

import { AppDispatch } from "../store";
import {
  Address,
  AddressValidation,
  BulkyItem,
  BulkyItemSchedulerPayload,
  ContactInformation,
  SchedulerWizardFormValues,
} from "src/interfaces";
import {
  loadScheduledItem as doLoadScheduledItem,
  saveScheduledItem as doSaveScheduledItem,
  cancelScheduledItems as doCancelScheduledItems,
  updateContactInfo as doUpdateContactInfo,
  loadPickupsInZone as doLoadPickupsInZone,
  validateAddress as doValidateAddress,
} from "../services";
import { PickupsInZone } from "src/interfaces/schedulerWizard";
import { setIsAuthenticated } from "./authReducer";

const initialState = {
  isLoading: false,
  isLoadingPickupsInZone: false,
  isSaving: false,
  isUpdatingContactInfo: false,
  scheduledPickupData: {} as SchedulerWizardFormValues,
  addressValidation: {} as AddressValidation,
  confirmationId: "",
  pickupsInZone: [] as PickupsInZone[],
  errors: {
    maxItemsPerRequestReached: false,
    addressValidation: false,
    maxPickupsPerLocationReached: false,
  },
};

const NAME = "bulkyItemsScheduler";

const bulkyItemsSchedulerSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    completeLoading: (state, action) => {
      state.isLoading = false;
      state.scheduledPickupData = action.payload;
    },
    failLoading: (state) => {
      state.isLoading = false;
    },
    startSaving: (state) => {
      state.isSaving = true;
    },
    completeSaving: (state, action) => {
      state.isSaving = false;
      state.confirmationId = action.payload;
    },
    failSaving: (state) => {
      state.isSaving = false;
    },
    startUpdatingContactInfo: (state) => {
      state.isUpdatingContactInfo = true;
    },
    completeUpdatingContactInfo: (state, action) => {
      state.isUpdatingContactInfo = false;
      state.scheduledPickupData.firstName = action.payload.firstName;
      state.scheduledPickupData.lastName = action.payload.lastName;
      state.scheduledPickupData.email = action.payload.email;
      state.scheduledPickupData.phoneNumber = action.payload.phoneNumber;
    },
    failUpdatingContactInfo: (state) => {
      state.isUpdatingContactInfo = false;
    },
    startCanceling: (state) => {
      state.isSaving = true;
    },
    completeCanceling: (state, action) => {
      state.isSaving = false;
      //  filter the items that were canceled based on the ids
      state.scheduledPickupData = update(
        state.scheduledPickupData,
        "items",
        (items: { [n: string]: BulkyItem }[]) =>
          map(items, (item) => {
            if (includes(action.payload, item.id)) {
              return merge(item, { isCancelled: true });
            }
            return item;
          })
      );
    },
    failCanceling: (state) => {
      state.isSaving = false;
    },
    setConfirmationId: (state, action) => {
      state.confirmationId = action.payload;
    },
    setQuantityExceeded: (state, action) => {
      state.errors.maxItemsPerRequestReached = action.payload;
    },
    setMaxPickupsPerLocationError: (state, action) => {
      state.errors.maxPickupsPerLocationReached = action.payload;
    },
    setAddressValidation: (state, action) => {
      state.isLoading = false;
      state.addressValidation = action.payload;
      state.errors.addressValidation = false;
    },
    setAddressValidationErrors: (state, action) => {
      state.isLoading = false;
      state.errors.addressValidation = action.payload;
    },
    startLoadingPickupsInZone: (state) => {
      state.isLoadingPickupsInZone = true;
    },
    completeLoadingPickupsInZone: (state, action) => {
      state.isLoadingPickupsInZone = false;
      state.pickupsInZone = action.payload;
    },
    failLoadingPickupsInZone: (state) => {
      state.isLoadingPickupsInZone = false;
    },
  },
});

export const loadScheduledItem = (id: string, vendorUrl: string) => (dispatch: AppDispatch) => {
  dispatch(bulkyItemsSchedulerSlice.actions.startLoading());
  const promise = doLoadScheduledItem(id, vendorUrl);
  promise
    .then((data) => {
      dispatch(setIsAuthenticated(true));
      dispatch(bulkyItemsSchedulerSlice.actions.completeLoading(data));
    })
    .catch(() => {
      dispatch(bulkyItemsSchedulerSlice.actions.failLoading());
    });
  return promise;
};

export const saveScheduledItem = (data: BulkyItemSchedulerPayload) => (dispatch: AppDispatch) => {
  dispatch(bulkyItemsSchedulerSlice.actions.startSaving());
  const promise = doSaveScheduledItem(data);
  promise
    .then((confirmationId) => {
      dispatch(bulkyItemsSchedulerSlice.actions.completeSaving(confirmationId.toString()));
    })
    .catch(() => {
      dispatch(bulkyItemsSchedulerSlice.actions.failSaving());
    });
  return promise;
};

export const cancelScheduledItems =
  (ids: number[], confirmationId: string) => (dispatch: AppDispatch) => {
    dispatch(bulkyItemsSchedulerSlice.actions.startCanceling());
    const promise = doCancelScheduledItems(ids, confirmationId);
    promise
      .then(() => {
        dispatch(bulkyItemsSchedulerSlice.actions.completeCanceling(ids));
      })
      .catch(() => {
        dispatch(bulkyItemsSchedulerSlice.actions.failCanceling());
      });
    return promise;
  };

export const validateAddress = (address: Address) => (dispatch: AppDispatch) => {
  dispatch(bulkyItemsSchedulerSlice.actions.startLoading());
  dispatch(bulkyItemsSchedulerSlice.actions.setAddressValidation(null));
  const promise = doValidateAddress(address);
  promise
    .then((data) => {
      dispatch(bulkyItemsSchedulerSlice.actions.setAddressValidation(data));
    })
    .catch((e) => {
      e.response.status === 400 &&
        e.response.data.code === "BadRequest" &&
        dispatch(bulkyItemsSchedulerSlice.actions.setAddressValidationErrors(true));
    });
  return promise;
};

export const updateContactInfo =
  (confirmationID: string, contactInfo: ContactInformation) => (dispatch: AppDispatch) => {
    dispatch(bulkyItemsSchedulerSlice.actions.startUpdatingContactInfo());
    const promise = doUpdateContactInfo(confirmationID, contactInfo);
    promise
      .then(() => {
        dispatch(bulkyItemsSchedulerSlice.actions.completeUpdatingContactInfo(contactInfo));
      })
      .catch(() => {
        dispatch(bulkyItemsSchedulerSlice.actions.failUpdatingContactInfo());
      });
    return promise;
  };

export const loadPickupsInZone =
  (zoneId: number, locationId?: number) => (dispatch: AppDispatch) => {
    dispatch(bulkyItemsSchedulerSlice.actions.startLoadingPickupsInZone());
    const promise = doLoadPickupsInZone(zoneId, locationId);
    promise
      .then((data) => {
        dispatch(bulkyItemsSchedulerSlice.actions.completeLoadingPickupsInZone(data));
      })
      .catch(() => {
        dispatch(bulkyItemsSchedulerSlice.actions.failLoadingPickupsInZone());
      });
    return promise;
  };

export const { setQuantityExceeded, setConfirmationId } = bulkyItemsSchedulerSlice.actions;
export default bulkyItemsSchedulerSlice.reducer;
